import React, { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { DialogoHost, conferma } from '../../components/Dialogo';
import TorneiAibvc from './TorneiAibvc';

// "Il mio AIBVC" (23/10/2026): la propria squadra (o quelle che si allenano), i compagni,
// gli allenatori e le giornate. Sede e convocazione compaiono quando lo staff le pubblica.
const GIORNI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const lungo = (iso) => { const d = new Date(iso + 'T12:00:00Z'); return `${GIORNI[d.getUTCDay()]} ${d.getUTCDate()} ${MESI[d.getUTCMonth()]}`; };
const breve = (iso) => { const d = new Date(iso + 'T12:00:00Z'); return `${GIORNI[d.getUTCDay()].slice(0, 3)} ${d.getUTCDate()} ${MESI[d.getUTCMonth()].slice(0, 3)}`; };
const errore = (e, def) => e?.response?.data?.error || def;

export default function MioAibvc() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [vista, setVista] = useState('squadra'); // 29/10: anche i tornei a coppie di aibvc.app (solo consultazione)
  const carica = () => api.get('/aibvc/mio').then(r => setD(r.data)).catch(e => setErr(errore(e, 'Non caricato')));
  useEffect(() => { carica(); }, []);
  const assenza = async (gs_id, assente) => {
    if (assente && !(await conferma('Segnalare allo staff che non puoi esserci?', { si: 'Sì, segnala' }))) return;
    setErr('');
    try { await api.post(`/aibvc/mio/assenza/${gs_id}`, { assente }); carica(); } catch (e) { setErr(errore(e, 'Non salvato')); }
  };
  if (!d) return <div style={s.pagina}><div style={s.vuoto}>{err || 'Caricamento…'}</div></div>;
  const nome = (user?.nome || '').split(' ')[0];
  const oggi = new Date().toISOString().slice(0, 10);
  const inSquadra = d.campionati.some(c => c.squadre.length || c.extra?.some(x => x.giorno >= oggi));

  return (
    <div style={s.pagina}>
      <DialogoHost />
      <div style={s.sopra}>AIBVC</div>
      <h1 style={s.titolo}>Ciao {nome}</h1>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 14px' }}>
        {[['squadra', 'Club Series'], ['tornei', 'Tornei CNT & L']].map(([k, l]) => <button key={k} onClick={() => setVista(k)} style={{ border: '1px solid var(--border2)', borderRadius: 999, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: vista === k ? 'var(--text)' : 'var(--bg-card)', color: vista === k ? 'var(--bg-card)' : 'var(--text-muted)' }}>{l}</button>)}
      </div>
      {vista === 'tornei' ? <TorneiAibvc atleta /> : <>
      {err && <div style={s.err}>{err}</div>}
      {!inSquadra && <PortaAperta iscritto={d.campionati.flatMap(c => c.in_attesa)} />}
      {d.campionati.map(c => <Campionato key={`${c.campionato.nome}-${c.campionato.stagione}`} d={c} oggi={oggi} assenza={assenza} titolo={d.campionati.length > 1 || inSquadra} />)}
      {inSquadra && <div style={{ ...s.dim, marginTop: 6 }}>Vedi solo la tua squadra e le giornate a cui sei assegnato/a. Le convocazioni compaiono quando lo staff le pubblica.</div>}
      </>}
    </div>
  );
}

// Chi non è in una squadra (23/10 titolare): non escluso, la porta è aperta, ma servono impegno,
// disciplina e costanza; rimando al percorso di crescita (in futuro anche il PDF).
function PortaAperta({ iscritto }) {
  return (
    <div style={{ ...s.card, ...s.cardPorta }}>
      <div style={{ fontSize: 18, fontWeight: 800 }}>Per ora non fai parte di una squadra AIBVC</div>
      {iscritto.length > 0 && <div style={s.dim}>La tua iscrizione ({iscritto.join(', ')}) è arrivata ed è stata vista dalla società.</div>}
      <p style={s.testo}>La porta resta aperta. Entrare in squadra però richiede <b>impegno, disciplina e costanza</b>: negli allenamenti di tutta la stagione, non solo nelle giornate di campionato. Le squadre si formano guardando proprio questo.</p>
      <p style={s.testo}>Il modo per arrivarci è il nostro <b>percorso di crescita</b>: allenati con continuità, ascolta i tuoi allenatori e fatti trovare pronto/a. Se vuoi capire da dove partire, parlane con il tuo allenatore.</p>
    </div>
  );
}

function Campionato({ d, oggi, assenza, titolo }) {
  if (!d.squadre.length && !d.extra?.some(x => x.giorno >= oggi)) return null;
  return (
    <>
      {titolo && <div style={s.titoloCamp}>{d.campionato.nome} {d.campionato.stagione}</div>}
      {d.squadre.map(q => {
        const prossime = q.giornate.filter(g => (g.giorno || g.data_fine || g.data_inizio) >= oggi);
        const passate = q.giornate.length - prossime.length;
        return (
          <div key={q.id} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 22 }}>
            <div style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div>
                  <div style={s.etichetta}>{q.alleno && q.atleta ? 'LA MIA SQUADRA · GIOCHI E ALLENI' : q.alleno ? 'SQUADRA CHE ALLENI' : 'LA MIA SQUADRA'}</div>
                  <div style={{ fontSize: 20, fontWeight: 800 }}>{q.nome}</div>
                  <div style={s.dim}>{q.categoria}</div>
                </div>
                <span style={s.badgeArancio}>{q.compagni.length + (q.atleta ? 1 : 0)} atleti</span>
              </div>
              <div style={s.etichetta2}>{q.allenatori.length > 1 ? 'ALLENATORI' : 'ALLENATORE'}</div>
              <div style={{ fontWeight: 600 }}>{q.allenatori.length ? q.allenatori.join(', ') : 'da definire'}</div>
              <div style={s.etichetta2}>{q.atleta ? 'COMPAGNI' : 'ATLETI'}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{q.compagni.map(c => <span key={c} style={s.chip}>{c}</span>)}</div>
            </div>

            <div style={s.etichettaFuori}>{q.atleta ? 'LE MIE GIORNATE' : 'GIORNATE DELLA SQUADRA'}</div>
            {prossime.map(g => {
              const quando = g.giorno ? lungo(g.giorno) : g.data_fine && g.data_fine !== g.data_inizio ? `${breve(g.data_inizio)} o ${breve(g.data_fine)}` : lungo(g.data_inizio);
              const stato = !g.pubblicata ? ['CONVOCAZIONI NON ANCORA USCITE', s.badgeGrigio]
                : !q.atleta ? [`${g.convocati} CONVOCATI`, s.badgeVerde]
                : g.convocato && g.assente ? ['HAI SEGNALATO CHE NON CI SEI', s.badgeArancioPieno]
                : g.convocato ? ['CONVOCATO/A', s.badgeVerde] : ['NON CONVOCATO/A', s.badgeGrigio];
              return (
                <div key={g.numero} style={{ ...s.card, ...(g.pubblicata && (g.convocato || !q.atleta) ? s.cardEvid : {}) }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 16, fontWeight: 800 }}>Giornata {g.numero}</div>
                    <span style={stato[1]}>{stato[0]}</span>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 15, marginTop: 4 }}>{quando}{g.ora ? ` · ore ${g.ora}` : ''}</div>
                  <div style={s.dim}>{g.pubblicata && (g.sede || g.citta) ? [g.sede, g.citta].filter(Boolean).join(', ') : 'Sede da definire'}</div>
                  {g.pubblicata && (g.sede || g.citta) && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                      <a style={s.btnScuro} target="_blank" rel="noreferrer" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([g.sede, g.citta].filter(Boolean).join(' '))}`}><MapPin size={15} /> Come arrivare</a>
                      {q.atleta && g.convocato && <button style={s.btnChiaro} onClick={() => assenza(g.gs_id, !g.assente)}>{g.assente ? 'Annulla: ci sono' : 'Non posso esserci'}</button>}
                    </div>
                  )}
                </div>
              );
            })}
            {!prossime.length && <div style={s.dim}>Nessuna giornata in programma.</div>}
            {passate > 0 && <div style={s.dim}>{passate} {passate === 1 ? 'giornata già giocata' : 'giornate già giocate'}.</div>}
          </div>
        );
      })}

      {d.extra?.filter(x => x.giorno >= oggi).map(x => (
        <div key={x.gs_id} style={{ ...s.card, ...s.cardEvid, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}><b>Convocato/a con {x.squadra}</b><span style={s.badgeVerde}>GIORNATA {x.numero}</span></div>
          <div style={{ fontWeight: 600, marginTop: 4 }}>{lungo(x.giorno)}{x.ora ? ` · ore ${x.ora}` : ''}</div>
          <div style={s.dim}>{[x.sede, x.citta].filter(Boolean).join(', ') || 'Sede da definire'}</div>
          <div style={{ marginTop: 8 }}><button style={s.btnChiaro} onClick={() => assenza(x.gs_id, !x.assente)}>{x.assente ? 'Annulla: ci sono' : 'Non posso esserci'}</button></div>
        </div>
      ))}
    </>
  );
}

const badge = { fontSize: 12, fontWeight: 700, padding: '5px 9px', borderRadius: 8, whiteSpace: 'nowrap' };
const s = {
  pagina: { maxWidth: 560, padding: '4px 0 40px', color: 'var(--text)' },
  sopra: { fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: '#C24B08' },
  titolo: { fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight: 800, letterSpacing: '1px', margin: '2px 0 14px' },
  cardPorta: { gap: 8, borderColor: '#F4C9A8', background: 'linear-gradient(180deg, #FFF7F1 0%, var(--bg-card) 70%)' },
  testo: { margin: 0, fontSize: 15, lineHeight: 1.55 },
  titoloCamp: { fontSize: 13, fontWeight: 800, letterSpacing: '0.06em', color: '#C24B08', textTransform: 'uppercase', margin: '6px 4px 10px' },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 18, padding: 16, display: 'flex', flexDirection: 'column', gap: 4 },
  cardEvid: { border: '2px solid #F4600C' },
  etichetta: { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' },
  etichetta2: { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginTop: 10 },
  etichettaFuori: { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', padding: '4px 4px 0' },
  chip: { fontSize: 14, background: 'var(--bg)', padding: '6px 10px', borderRadius: 9 },
  dim: { fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.45 },
  badgeArancio: { ...badge, background: '#FFF0E6', color: '#8A3407' },
  badgeArancioPieno: { ...badge, background: '#FFF1E0', color: '#8A3B00' },
  badgeVerde: { ...badge, background: '#E7F6EC', color: '#1F7A45' },
  badgeGrigio: { ...badge, background: 'var(--bg-input)', color: 'var(--text-muted)' },
  btnScuro: { display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '0 14px', borderRadius: 12, background: 'var(--text)', color: 'var(--bg-card)', fontWeight: 700, fontSize: 14, textDecoration: 'none' },
  btnChiaro: { minHeight: 44, padding: '0 14px', borderRadius: 12, border: '1px solid var(--border2)', background: 'var(--bg-card)', color: 'var(--text)', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  err: { background: '#FDE8E8', color: '#9B1C1C', borderRadius: 10, padding: '9px 12px', fontSize: 14, margin: '8px 0' },
  vuoto: { color: 'var(--text-muted)', padding: '40px 0', textAlign: 'center' },
};
